import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  Text,
  Spinner,
  HStack,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiDollarSign,
  FiShoppingCart,
  FiPackage,
} from "react-icons/fi";
import api from "../lib/api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toaster } from "../components/ui/toaster";

interface SalesOverview {
  total_revenue: number;
  total_cost: number;
  total_transactions: number;
  unique_products: number;
  unique_locations: number;
  average_transaction_value: number;
}

interface TrendData {
  period: string;
  revenue: number;
  cost: number;
  transactions: number;
}

interface LocationData {
  city?: string;
  lga?: string;
  location_id: number;
  revenue: number;
  cost: number;
  transactions: number;
  average_transaction_value: number;
}

interface PeakHourData {
  hour: number;
  transactions: number;
  revenue: number;
}

interface PeakDayData {
  day_of_week: number;
  day_name: string;
  transactions: number;
  revenue: number;
  average_transaction_value: number;
}

interface TransactionDistribution {
  category: string;
  range: string;
  count: number;
  percentage: number;
  [key: string]: any;
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

export default function SalesAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [peakHours, setPeakHours] = useState<PeakHourData[]>([]);
  const [peakDays, setPeakDays] = useState<PeakDayData[]>([]);
  const [distribution, setDistribution] = useState<TransactionDistribution[]>(
    [],
  );

  const [period, setPeriod] = useState("daily");
  const [dateType, setDateType] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [states, setStates] = useState<string[]>([]);

  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState({
    dateType: "30d",
    startDate: "",
    endDate: "",
    state: "",
    period: "daily",
  });

  // Location table state
  const [locationSearch, setLocationSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [appliedFilters]);

  const fetchStates = async () => {
    try {
      const response = await api.get("/analytics/states");
      setStates(response.data.data || response.data || []);
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  };

  const getDateRange = (filterType: string) => {
    if (
      filterType === "custom" &&
      appliedFilters.startDate &&
      appliedFilters.endDate
    ) {
      return {
        start_date: appliedFilters.startDate,
        end_date: appliedFilters.endDate,
      };
    }

    const end = new Date();
    const start = new Date();

    switch (filterType) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      case "180d":
        start.setDate(end.getDate() - 180);
        break;
      case "this-month":
        start.setDate(1);
        break;
      case "last-month":
        start.setMonth(end.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      dateType: dateType,
      startDate: startDate,
      endDate: endDate,
      state: stateFilter,
      period: period,
    });
    setCurrentPage(1);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange(appliedFilters.dateType);
      const params = new URLSearchParams();
      params.append("start_date", start_date);
      params.append("end_date", end_date);
      if (appliedFilters.state && appliedFilters.state.trim() !== "") {
        params.append("state", appliedFilters.state);
      }
      const queryString = params.toString();

      // Determine which location endpoint to use
      const locationEndpoint =
        appliedFilters.state && appliedFilters.state.trim() !== ""
          ? `/analytics/sales/by-lga?${queryString}`
          : `/analytics/sales/by-location?${queryString}`;

      // Fetch all analytics data
      const [
        overviewRes,
        trendsRes,
        locationsRes,
        peakHoursRes,
        peakDaysRes,
        distributionRes,
      ] = await Promise.all([
        api.get(`/analytics/sales/overview?${queryString}`),
        api.get(
          `/analytics/sales/trends?period=${appliedFilters.period}&${queryString}`,
        ),
        api.get(locationEndpoint),
        api.get(`/analytics/sales/peak-hours?${queryString}`),
        api.get(`/analytics/sales/peak-days?${queryString}`),
        api.get(`/analytics/sales/transaction-distribution?${queryString}`),
      ]);

      // Handle nested response structure from APIResponse wrapper
      const overviewData = overviewRes.data.data || overviewRes.data;
      const trendsData =
        trendsRes.data.data?.data ||
        trendsRes.data.data ||
        trendsRes.data ||
        [];
      const locationsData =
        locationsRes.data.data?.locations ||
        locationsRes.data.locations ||
        locationsRes.data ||
        [];
      const peakHoursData =
        peakHoursRes.data.data?.peak_hours ||
        peakHoursRes.data.peak_hours ||
        peakHoursRes.data ||
        [];
      const peakDaysData =
        peakDaysRes.data.data?.peak_days ||
        peakDaysRes.data.peak_days ||
        peakDaysRes.data ||
        [];
      const distributionData =
        distributionRes.data.data?.distribution ||
        distributionRes.data.distribution ||
        distributionRes.data ||
        [];

      setOverview(overviewData);
      setTrends(Array.isArray(trendsData) ? trendsData : []);
      setLocations(Array.isArray(locationsData) ? locationsData : []);
      setPeakHours(Array.isArray(peakHoursData) ? peakHoursData : []);
      setPeakDays(Array.isArray(peakDaysData) ? peakDaysData : []);
      setDistribution(Array.isArray(distributionData) ? distributionData : []);
    } catch (error: any) {
      toaster.create({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to fetch analytics data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatCompactCurrency = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-US").format(value);
  };

  if (loading) {
    return (
      <Flex height="400px" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box p={{ base: 2, md: 6 }}>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <Box>
          <Heading size="xl" mb={2}>
            Sales & Revenue Analytics
          </Heading>
          <Text color="gray.600">
            Comprehensive overview of sales performance, revenue trends, and
            business metrics
          </Text>
        </Box>

        {/* Filters */}
        <Card.Root>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
              <Field.Root>
                <Field.Label>State</Field.Label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    backgroundColor: "white",
                  }}>
                  <option value="">All States</option>
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </Field.Root>
              <Field.Root>
                <Field.Label>Date Range</Field.Label>
                <select
                  value={dateType}
                  onChange={(e) => setDateType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    backgroundColor: "white",
                  }}>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="180d">Last 180 Days</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </Field.Root>
              {dateType === "custom" && (
                <>
                  <Field.Root>
                    <Field.Label>Start Date</Field.Label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px",
                      }}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>End Date</Field.Label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px",
                      }}
                    />
                  </Field.Root>
                </>
              )}
              <Field.Root>
                <Field.Label>Trend Period</Field.Label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    backgroundColor: "white",
                  }}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field.Root>
            </SimpleGrid>
            <Box mt={4}>
              <button
                onClick={handleApplyFilters}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#3182CE",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}>
                Apply Filters
              </button>
            </Box>
          </Card.Body>
        </Card.Root>

        {/* Overview Cards */}
        {overview && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
            <Card.Root>
              <Card.Body>
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box flex="1" minWidth="0">
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Total Revenue
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="bold"
                      color="green.600"
                      lineHeight="1.2"
                      wordBreak="break-word">
                      {formatCurrency(overview.total_revenue)}
                    </Text>
                  </Box>
                  <Box
                    p={2}
                    bg="green.50"
                    color="green.600"
                    borderRadius="md"
                    fontSize="xl"
                    flexShrink={0}>
                    <FiDollarSign />
                  </Box>
                </Flex>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body>
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box flex="1" minWidth="0">
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Total Cost
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="bold"
                      color="orange.600"
                      lineHeight="1.2"
                      wordBreak="break-word">
                      {formatCurrency(overview.total_cost)}
                    </Text>
                  </Box>
                  <Box
                    p={2}
                    bg="orange.50"
                    color="orange.600"
                    borderRadius="md"
                    fontSize="xl"
                    flexShrink={0}>
                    <FiTrendingUp />
                  </Box>
                </Flex>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body>
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box flex="1" minWidth="0">
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Total Transactions
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="bold"
                      lineHeight="1.2"
                      wordBreak="break-word">
                      {formatNumber(overview.total_transactions)}
                    </Text>
                  </Box>
                  <Box
                    p={2}
                    bg="purple.50"
                    color="purple.600"
                    borderRadius="md"
                    fontSize="xl"
                    flexShrink={0}>
                    <FiShoppingCart />
                  </Box>
                </Flex>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Body>
                <Flex justify="space-between" align="flex-start" gap={4}>
                  <Box flex="1" minWidth="0">
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Avg Transaction
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="bold"
                      lineHeight="1.2"
                      wordBreak="break-word">
                      {formatCurrency(overview.average_transaction_value)}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {overview.unique_products} products
                    </Text>
                  </Box>
                  <Box
                    p={2}
                    bg="orange.50"
                    color="orange.600"
                    borderRadius="md"
                    fontSize="xl"
                    flexShrink={0}>
                    <FiPackage />
                  </Box>
                </Flex>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        )}

        {/* Revenue Trends Chart */}
        <Card.Root>
          <Card.Header>
            <Heading size="md">Revenue & Cost Trends</Heading>
          </Card.Header>
          <Card.Body>
            {trends && trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={trends}
                  margin={{ left: 10, right: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 12, textAnchor: "end" }}
                    angle={-45}
                    height={60}
                  />
                  <YAxis
                    tickFormatter={(value) => formatCompactCurrency(value)}
                    width={60}
                    tick={{ fontSize: 12 }}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    formatter={((value: number | undefined) =>
                      value !== undefined ? formatCurrency(value) : ""
                    ) as any}
                  />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#48BB78"
                    strokeWidth={2}
                    name="Revenue"
                  />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke="#F56565"
                    strokeWidth={2}
                    name="Cost"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Flex height="300px" alignItems="center" justifyContent="center">
                <Text color="gray.500">
                  No trend data available for the selected period
                </Text>
              </Flex>
            )}
          </Card.Body>
        </Card.Root>

        {/* Location Performance */}
        <Card.Root>
          <Card.Header>
            <Heading size="md">
              {appliedFilters.state && appliedFilters.state.trim() !== ""
                ? `Performance by LGA in ${appliedFilters.state}`
                : "Performance by State"}
            </Heading>
          </Card.Header>
          <Card.Body>
            {locations && locations.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={locations.slice(0, 10)}
                  margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={
                      appliedFilters.state && appliedFilters.state.trim() !== ""
                        ? "lga"
                        : "states"
                    }
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(value) => formatCompactCurrency(value)}
                    width={60}
                    tick={{ fontSize: 12 }}
                    domain={[0, "auto"]}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div
                            style={{
                              backgroundColor: "white",
                              padding: "12px",
                              border: "1px solid #E2E8F0",
                              borderRadius: "6px",
                              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                            }}>
                            <p
                              style={{
                                fontWeight: "bold",
                                marginBottom: "8px",
                              }}>
                              {label}
                            </p>
                            <p
                              style={{ color: "#48BB78", marginBottom: "4px" }}>
                              Revenue: {formatCurrency(data.revenue)}
                            </p>
                            <p style={{ color: "#718096" }}>
                              Transactions: {formatNumber(data.transactions)}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#48BB78" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex height="300px" alignItems="center" justifyContent="center">
                <Text color="gray.500">
                  No location data available
                  {appliedFilters.state && appliedFilters.state.trim() !== ""
                    ? ` for ${appliedFilters.state}`
                    : ""}
                </Text>
              </Flex>
            )}
          </Card.Body>
        </Card.Root>

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
          {/* Peak Hours */}
          <Card.Root>
            <Card.Header>
              <Heading size="md">Peak Hours</Heading>
            </Card.Header>
            <Card.Body>
              {peakHours && peakHours.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="hour"
                      label={{
                        value: "Hour of Day",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis domain={[0, "auto"]} />
                    <Tooltip />
                    <Bar
                      dataKey="transactions"
                      fill="#8884D8"
                      name="Transactions"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Flex
                  height="250px"
                  alignItems="center"
                  justifyContent="center">
                  <Text color="gray.500">No peak hour data available</Text>
                </Flex>
              )}
            </Card.Body>
          </Card.Root>

          {/* Peak Days */}
          <Card.Root>
            <Card.Header>
              <Heading size="md">Peak Days</Heading>
            </Card.Header>
            <Card.Body>
              {peakDays && peakDays.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={peakDays} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day_name" tick={{ fontSize: 12 }} />
                    <YAxis
                      tickFormatter={(value) => formatCompactCurrency(value)}
                      width={60}
                      tick={{ fontSize: 12 }}
                      domain={[0, "auto"]}
                    />
                    <Tooltip
                      formatter={((value: number | undefined) =>
                        value !== undefined ? formatCurrency(value) : ""
                      ) as any}
                    />
                    <Bar dataKey="revenue" fill="#82CA9D" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Flex
                  height="250px"
                  alignItems="center"
                  justifyContent="center">
                  <Text color="gray.500">No peak day data available</Text>
                </Flex>
              )}
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Transaction Value Distribution */}
        <Card.Root>
          <Card.Header>
            <Heading size="md">Transaction Value Distribution</Heading>
          </Card.Header>
          <Card.Body>
            {distribution && distribution.length > 0 ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={distribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ category, percentage }: any) =>
                        `${category}: ${percentage}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count">
                      {distribution.map((_entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <VStack align="stretch" justify="center">
                  {distribution.map((item, index) => (
                    <HStack
                      key={index}
                      justify="space-between"
                      p={2}
                      bg="gray.50"
                      borderRadius="md">
                      <HStack>
                        <Box
                          w={4}
                          h={4}
                          bg={COLORS[index % COLORS.length]}
                          borderRadius="sm"
                        />
                        <Box>
                          <Text fontWeight="medium">{item.category}</Text>
                          <Text fontSize="xs" color="gray.600">
                            {item.range}
                          </Text>
                        </Box>
                      </HStack>
                      <Box textAlign="right">
                        <Text fontWeight="bold">
                          {formatNumber(item.count)}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {item.percentage}%
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </SimpleGrid>
            ) : (
              <Flex height="250px" alignItems="center" justifyContent="center">
                <Text color="gray.500">
                  No transaction distribution data available
                </Text>
              </Flex>
            )}
          </Card.Body>
        </Card.Root>

        {/* Top Locations Table */}
        <Card.Root>
          <Card.Header>
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <Heading size="md">
                {appliedFilters.state && appliedFilters.state.trim() !== ""
                  ? `Top Performing LGAs in ${appliedFilters.state}`
                  : "Top Performing States"}
              </Heading>
              <input
                placeholder={
                  appliedFilters.state && appliedFilters.state.trim() !== ""
                    ? "Search LGAs..."
                    : "Search states..."
                }
                value={locationSearch}
                onChange={(e) => {
                  setLocationSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  width: "100%",
                  maxWidth: "300px",
                }}
              />
            </Flex>
          </Card.Header>
          <Card.Body>
            <Box overflowX="auto">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
                    <th style={{ padding: "12px", textAlign: "left" }}>
                      {appliedFilters.state &&
                      appliedFilters.state.trim() !== ""
                        ? "LGA"
                        : "State"}
                    </th>
                    <th style={{ padding: "12px", textAlign: "right" }}>
                      Revenue
                    </th>
                    <th style={{ padding: "12px", textAlign: "right" }}>
                      Transactions
                    </th>
                    <th style={{ padding: "12px", textAlign: "right" }}>
                      Avg Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {locations
                    .filter((loc) => {
                      const searchTerm = locationSearch.toLowerCase();
                      const locationName =
                        appliedFilters.state &&
                        appliedFilters.state.trim() !== ""
                          ? (loc.lga || "").toLowerCase()
                          : ((loc as any).states || "").toLowerCase();
                      return locationName.includes(searchTerm);
                    })
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((location, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: "1px solid #E2E8F0" }}>
                        <td style={{ padding: "12px" }}>
                          {appliedFilters.state &&
                          appliedFilters.state.trim() !== ""
                            ? location.lga
                            : (location as any).states}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "right",
                            fontWeight: "bold",
                            color: "#48BB78",
                          }}>
                          {formatCurrency(location.revenue)}
                        </td>
                        <td style={{ padding: "12px", textAlign: "right" }}>
                          {formatNumber(location.transactions)}
                        </td>
                        <td style={{ padding: "12px", textAlign: "right" }}>
                          {formatCurrency(location.average_transaction_value)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Box>

            {/* Pagination Controls */}
            <Flex justify="space-between" align="center" mt={4}>
              <Text fontSize="sm" color="gray.600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  locations.filter((l) => {
                    const searchTerm = locationSearch.toLowerCase();
                    const locationName = (l.city || l.lga || "").toLowerCase();
                    return (
                      locationName.includes(searchTerm) ||
                      l.location_id.toString().includes(locationSearch)
                    );
                  }).length,
                )}{" "}
                of{" "}
                {
                  locations.filter((l) => {
                    const searchTerm = locationSearch.toLowerCase();
                    const locationName = (l.city || l.lga || "").toLowerCase();
                    return (
                      locationName.includes(searchTerm) ||
                      l.location_id.toString().includes(locationSearch)
                    );
                  }).length
                }{" "}
                entries
              </Text>
              <HStack gap={2}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    backgroundColor: currentPage === 1 ? "#F7FAFC" : "white",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    color: currentPage === 1 ? "#A0AEC0" : "inherit",
                  }}>
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={
                    currentPage * itemsPerPage >=
                    locations.filter((l) => {
                      const searchTerm = locationSearch.toLowerCase();
                      const locationName = (
                        l.city ||
                        l.lga ||
                        ""
                      ).toLowerCase();
                      return (
                        locationName.includes(searchTerm) ||
                        l.location_id.toString().includes(locationSearch)
                      );
                    }).length
                  }
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    backgroundColor:
                      currentPage * itemsPerPage >=
                      locations.filter((l) => {
                        const searchTerm = locationSearch.toLowerCase();
                        const locationName = (
                          l.city ||
                          l.lga ||
                          ""
                        ).toLowerCase();
                        return (
                          locationName.includes(searchTerm) ||
                          l.location_id.toString().includes(locationSearch)
                        );
                      }).length
                        ? "#F7FAFC"
                        : "white",
                    cursor:
                      currentPage * itemsPerPage >=
                      locations.filter((l) => {
                        const searchTerm = locationSearch.toLowerCase();
                        const locationName = (
                          l.city ||
                          l.lga ||
                          ""
                        ).toLowerCase();
                        return (
                          locationName.includes(searchTerm) ||
                          l.location_id.toString().includes(locationSearch)
                        );
                      }).length
                        ? "not-allowed"
                        : "pointer",
                    color:
                      currentPage * itemsPerPage >=
                      locations.filter((l) => {
                        const searchTerm = locationSearch.toLowerCase();
                        const locationName = (
                          l.city ||
                          l.lga ||
                          ""
                        ).toLowerCase();
                        return (
                          locationName.includes(searchTerm) ||
                          l.location_id.toString().includes(locationSearch)
                        );
                      }).length
                        ? "#A0AEC0"
                        : "inherit",
                  }}>
                  Next
                </button>
              </HStack>
            </Flex>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
}
